import { useCallback, useEffect, useState } from "react";
import api from "../api/axios";
import AppShell from "../components/AppShell";
import DataTable from "../components/DataTable";
import RowIconAction from "../components/RowIconAction";
import { Ban, CircleCheck, Pencil, Trash2 } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", district: "", distributor: "", status: "active" };

export default function SalesPersonManagement() {
  const [salesPersons, setSalesPersons] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [peopleResponse, distributorResponse] = await Promise.all([
        api.get("/sales-persons"),
        api.get("/sales-persons/distributors"),
      ]);
      setSalesPersons(peopleResponse.data.salesPersons);
      setDistricts(distributorResponse.data.districts);
      setDistributors(distributorResponse.data.distributors);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load the sales team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setError("");
    setShowForm(true);
  };

  const openEdit = (person) => {
    setEditing(person);
    setForm({
      name: person.name,
      email: person.email,
      password: "",
      district: person.distributor?.district?._id || person.distributor?.district || "",
      distributor: person.distributor?._id || person.distributor,
      status: person.status,
    });
    setError("");
    setShowForm(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) await api.put(`/sales-persons/${editing.id}`, payload);
      else await api.post("/sales-persons", payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Could not save the sales person");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (person) => {
    if (!window.confirm(`Remove ${person.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales-persons/${person.id}`);
      setSalesPersons((items) => items.filter((item) => item.id !== person.id));
    } catch (err) {
      setError(err.response?.data?.message || "Could not remove the sales person");
    }
  };

  const toggleStatus = async (person) => {
    try {
      const { data } = await api.put(`/sales-persons/${person.id}`, {
        status: person.status === "active" ? "suspended" : "active",
      });
      setSalesPersons((items) => items.map((item) => item.id === person.id ? data.salesPerson : item));
    } catch (err) {
      setError(err.response?.data?.message || "Could not update status");
    }
  };

  return <AppShell>
    <div style={styles.header}>
      <div><div style={styles.eyebrow}>DISTRIBUTION NETWORK</div><h1 style={styles.title}>Sales Team</h1><p style={styles.subtitle}>Create portal access and assign every sales person to one distributor.</p></div>
      <button style={styles.primary} onClick={openCreate}>+ Add sales person</button>
    </div>
    {error && <div style={styles.error}>{error}</div>}
    {loading && <div className="data-table-status" role="status">Loading sales team...</div>}
    {!loading && !salesPersons.length ? <div style={styles.empty}>No sales people yet.</div> : <DataTable headers={["Sales person", "Assigned distributor", "Status", "Access", "Actions"]} wrapStyle={styles.tableWrap} tableStyle={styles.table}>{salesPersons.map((person) => <tr key={person.id}><td><strong>{person.name}</strong><small>{person.email}</small></td><td>{person.distributor?.businessDetails?.businessName || person.distributor?.basicInfo?.name || "-"}</td><td><span style={person.status === "active" ? styles.active : styles.suspended}>{person.status}</span></td><td>{person.accessSentAt ? <span style={styles.sent}>Sent</span> : <span style={styles.muted}>Not emailed</span>}</td><td><div className="row-actions"><RowIconAction label={person.status === "active" ? `Suspend ${person.name}` : `Reactivate ${person.name}`} icon={person.status === "active" ? Ban : CircleCheck} onClick={() => toggleStatus(person)} /><RowIconAction label={`Edit ${person.name}`} icon={Pencil} onClick={() => openEdit(person)} /><RowIconAction label={`Delete ${person.name}`} icon={Trash2} onClick={() => remove(person)} danger /></div></td></tr>)}</DataTable>}
    {showForm && <div style={styles.overlay}><div style={styles.modal}><div style={styles.modalHeader}><h2 style={styles.modalTitle}>{editing ? "Edit sales person" : "Add sales person"}</h2><button style={styles.close} onClick={() => setShowForm(false)}>x</button></div><form onSubmit={save} style={styles.form}><Field label="Full name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} required /><Field label="Email for portal login" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} required /><label style={styles.label}>District<select required value={form.district} onChange={(event) => setForm({ ...form, district: event.target.value, distributor: "" })} style={styles.input}><option value="">Select district...</option>{districts.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label style={styles.label}>Assigned distributor<select required disabled={!form.district} value={form.distributor} onChange={(event) => setForm({ ...form, distributor: event.target.value })} style={styles.input}><option value="">{form.district ? "Select distributor..." : "Select district first"}</option>{distributors.filter((item) => String(item.district?._id || item.district) === String(form.district)).map((item) => <option key={item.id} value={item.id}>{item.businessName} - {item.name}</option>)}</select></label><Field label={editing ? "New password (optional)" : "Password (optional)"} type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value })} /><label style={styles.label}>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} style={styles.input}><option value="active">Active</option><option value="suspended">Suspended</option></select></label><div style={styles.actions}><button type="button" style={styles.secondary} onClick={() => setShowForm(false)}>Cancel</button><button type="submit" disabled={saving} style={styles.primary}>{saving ? "Saving..." : editing ? "Save changes" : "Create & send access"}</button></div></form></div></div>}
  </AppShell>;
}

function Field({ label, value, onChange, type = "text", required }) {
  return <label style={styles.label}>{label}<input required={required} type={type} value={value} onChange={(event) => onChange(event.target.value)} style={styles.input} /></label>;
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", gap: 24, alignItems: "flex-start", marginBottom: 24 },
  eyebrow: { color: "var(--signal)", fontSize: 11, letterSpacing: 1.5, fontWeight: 700 },
  title: { fontFamily: "var(--font-display)", fontSize: 30, margin: "5px 0 0" },
  subtitle: { color: "var(--slate-500)", fontSize: 14, marginTop: 6 },
  primary: { background: "var(--ink)", color: "#fff", border: 0, borderRadius: 4, padding: "10px 16px", fontWeight: 600 },
  secondary: { background: "#fff", color: "var(--ink)", border: "1px solid var(--line)", borderRadius: 4, padding: "10px 16px" },
  error: { color: "#a8402c", background: "rgba(217,99,78,.1)", border: "1px solid var(--danger)", padding: "10px 14px", borderRadius: 4, marginBottom: 14 },
  muted: { color: "var(--slate-500)" },
  tableWrap: { background: "#fff", border: "1px solid var(--line)", borderRadius: "var(--radius)", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  empty: { padding: 24, color: "var(--slate-500)" },
  active: { color: "var(--ok)", background: "rgba(79,169,122,.12)", padding: "4px 8px", borderRadius: 3, fontSize: 12 },
  suspended: { color: "var(--danger)", background: "rgba(217,99,78,.12)", padding: "4px 8px", borderRadius: 3, fontSize: 12 },
  sent: { color: "var(--ok)", fontSize: 12 },
  link: { background: "none", border: 0, color: "var(--ink)", textDecoration: "underline", marginRight: 10, fontSize: 12 },
  danger: { background: "none", border: 0, color: "var(--danger)", textDecoration: "underline", fontSize: 12 },
  overlay: { position: "fixed", inset: 0, background: "rgba(20,24,31,.54)", display: "grid", placeItems: "center", padding: 20, zIndex: 5 },
  modal: { background: "#fff", width: "min(520px, 100%)", borderRadius: 6, padding: 24 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  modalTitle: { fontFamily: "var(--font-display)", margin: 0 },
  close: { background: "none", border: 0, fontSize: 20 },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  label: { display: "flex", flexDirection: "column", gap: 6, color: "var(--slate-500)", fontSize: 12 },
  input: { border: "1px solid var(--line)", borderRadius: 4, padding: "10px", font: "inherit", color: "var(--ink)" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 },
};